import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { versionDe } from '../../api/ops/disponibilidad';
import type { CambiosNegocio, ContratoEncargo, DatosNegocio, DireccionAnterior, Disponibilidad, Exportacion, PaginaPrivacidad, Sector, Sede } from '../../api/ops/negocio';
import { ErrorApi } from '../../api/problema';
import { correoDeSoporte } from '../../config';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError, Hueco } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera, type TarjetaAyuda } from '../../marco/cabecera';
import { dibujoDe } from '../../marco/iconosMenu';
import { usePermisos } from '../../permisos/permisos';
import { Horario } from '../horario/Horario';
import { resumenRegla } from '../horario/regla';
import { ReglaReserva, type ReglaConVersion } from '../horario/ReglaReserva';
import type { PropsPantalla } from '../tipos';
import {
    COMUNIDADES_ES,
    FICHA_VACIA,
    FORMATO_SLUG,
    IDIOMAS,
    PAISES,
    baseDeDireccion,
    datosDeFicha,
    direccionDeSede,
    enlaceOposicion,
    erroresContacto,
    erroresPrivacidad,
    erroresSede,
    estadoSlug,
    etiquetaDe,
    fichaDe,
    mesYAno,
    normalizarSlug,
    TEXTO_SIN_CAMBIOS_SLUG,
    TEXTOS_SLUG,
    textosCambiosSlug,
    opcionesSector,
    PLANTILLA_PRIVACIDAD,
    PRIVACIDAD_VACIA,
    privacidadDeLaPagina,
    queGestiona,
    resumenPrivacidad,
    resumenSedes,
    sinEsquema,
    telefonoE164,
    textosExportacion,
    urlDePolitica,
    zonaDe,
    type FichaPrivacidad,
    type FichaSede,
    accesosDesarrolladores,
    cifrasDeAccesos,
    type CifraAcceso,
} from './logica';
import { Seleccion } from './Seleccion';
import './negocio.css';

/**
 * Negocio › Datos del negocio (1.14): tarjetas de ajustes (componentes.md §5d) que abren su RV Panel a la derecha.
 * Propietario y Administrador cambian (`negocio.ajustes.editar`, `negocio.slug.cambiar`); quien solo consulta lo ve en
 * solo lectura; exportar, solo el propietario (`negocio.exportar`). El negocio y cada sede se editan con la versión leída
 * (`If-Match`, G228); si otra persona los cambió antes (412), se vuelven a cargar.
 *
 * «Nombre y sector» elige de la lista cerrada de la API (`listBusinessSectors`, G270). «Privacidad» (G195, G267, G268,
 * G271): la página que Reservaria publica sola desde el alta sale «Sin completar» hasta que el negocio completa la
 * plantilla («Usar la de Reservaria»: razón social, NIF, dirección y email obligatorios) o enlaza la suya («Tengo la
 * mía», solo https); debajo, el contrato de encargo (G201) para quien cambia los ajustes. Se abre también con
 * `?pantalla=privacidad` (el paso «Tu página de privacidad» de la puesta en marcha).
 *
 * «Cuándo se puede reservar» (3.3, Agenda y Disponibilidad) es la tarjeta de la entrada `reglas_reserva` (`nested_as: card`,
 * G130, G244): solo sale si la manda el servidor y abre su RV Panel (también con `?pantalla=antelacion`). La zona horaria
 * se ve aquí y se cambia en Horario (G255).
 *
 * «Para desarrolladores» (tarjeta de Web pública, G635) lleva a su ruta: las claves de API al final de Widgets (13.6).
 *
 * Fuera de esta entrega (esperan su módulo): cambiar «Qué gestiona» (con Planes y Stripe) y «Políticas de pago» (Pagos).
 */
type Lado = 'id' | 'ge' | 'se' | 'sede' | 'co' | 'slug' | 'priv' | 'regla' | 'pagos' | 'dev' | 're' | 'ex';

const COLOR_MARCA = /^#[0-9A-Fa-f]{6}$/;
const AYUDA_DATOS: TarjetaAyuda = { icono: 'store', titulo: 'Datos generales', texto: 'Lo que ven tus clientes en la web, los mensajes y los pases. Toca una tarjeta para cambiarla.' };
const AYUDA_SEDES: TarjetaAyuda = { icono: 'map-pin', titulo: 'Sedes', texto: 'Si tienes varios locales, cada uno con su dirección. Una sede no se borra: se archiva y se puede reactivar.' };
// Sin «Hasta 3 cambios al año»: el tope es la pregunta 13, aún abierta.
const AYUDA_DIRECCION: TarjetaAyuda = { icono: 'link', titulo: 'Dirección web', texto: 'La de tu web, tus widgets y tus enlaces. Las antiguas siguen llevando a la nueva.' };
const AYUDA_PRIVACIDAD: TarjetaAyuda = { icono: 'shield', titulo: 'Privacidad', texto: 'Tus clientes tienen que poder leer qué haces con sus datos. Tus páginas enlazan «Privacidad de» tu negocio junto a la de Reservaria.' };
const AYUDA_EXPORTAR: TarjetaAyuda = { icono: 'download', titulo: 'Exportar los datos', texto: 'Te preparamos un archivo con todo y te avisamos por email con un enlace que caduca.' };

export const TEXTO_CONFLICTO = 'Alguien ha cambiado estos datos mientras tanto. Los he vuelto a cargar: revisa y guarda otra vez.';

const IMPACTO: readonly (readonly [NombreIcono, string, string | null])[] = [
    ['history', 'Redirección desde la antigua', null],
    ['qr-code', 'Enlaces y QR impresos', 'Siguen funcionando porque llevan a la nueva. Cuando los reimprimas, usa la nueva.'],
    ['code', 'Widgets en tu web', 'Siguen funcionando sin tocar nada.'],
    ['message-circle', 'Mensajes ya enviados', 'Sus enlaces siguen abriendo la reserva o el pase.'],
];

/** Mensajes de la API por campo (`errors[].field`). */
function erroresDeCampos(e: unknown): Record<string, string> {
    if (!(e instanceof ErrorApi)) {
        return {};
    }
    const r: Record<string, string> = {};
    for (const x of e.errores) {
        if (typeof x.field === 'string') {
            r[x.field] = x.message ?? 'No es válido.';
        }
    }
    return r;
}

interface Panel {
    titulo: string;
    eyebrow: string;
    sub?: string;
    icono: NombreIcono;
    accion?: string;
    accionIcono?: NombreIcono;
    onAccion?: () => void;
    inactiva?: boolean;
    cerrar?: string;
    chip?: string;
    chipEstado?: EstadoChip;
    cuerpo: ReactNode;
}

function Linea({ icono, clave, valor, texto, chip, chipEstado = 'neutral' }: { icono: NombreIcono; clave?: string; valor?: string; texto?: string; chip?: string; chipEstado?: EstadoChip }) {
    return (
        <div className="rv-neg-linea">
            <span className="rv-neg-linea__icono rv-neg-linea__icono--plano">
                <Icono nombre={icono} />
            </span>
            <span className="rv-neg-linea__texto">
                {clave !== undefined && <span className="rv-neg-linea__k">{clave}</span>}
                {valor !== undefined && <strong>{valor}</strong>}
                {texto !== undefined && <span>{texto}</span>}
            </span>
            {chip !== undefined && <Chip estado={chipEstado}>{chip}</Chip>}
        </div>
    );
}

function DatosDelNegocio({ ops, negocio, membresia, soloLectura, movil, recargar, busqueda, anidadas, navegar }: PropsPantalla) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const puedeEditar = permisos.puede('negocio.ajustes.editar') && !soloLectura;
    const puedeSlug = permisos.puede('negocio.slug.cambiar') && !soloLectura;
    const puedeExportar = permisos.puede('negocio.exportar');

    const [biz, setBiz] = useState<{ datos: DatosNegocio; version: string | null } | null>(null);
    const [sedes, setSedes] = useState<Sede[]>([]);
    const [historial, setHistorial] = useState<DireccionAnterior[]>([]);
    const [bloques, setBloques] = useState<string[]>([]);
    const [errorCarga, setErrorCarga] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);

    const [lado, setLado] = useState<Lado | null>(null);
    const [abierto, setAbierto] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [ventana, setVentana] = useState<'impacto' | 'archivar' | null>(null);

    const [nombre, setNombre] = useState('');
    const [email, setEmail] = useState('');
    const [telefono, setTelefono] = useState('');
    const [idioma, setIdioma] = useState('es-ES');
    const [intentado, setIntentado] = useState(false);
    const [errApi, setErrApi] = useState<Record<string, string>>({});

    const [ficha, setFicha] = useState<FichaSede>(FICHA_VACIA);
    const [sedeAbierta, setSedeAbierta] = useState<{ id: string; version: string | null; status: Sede['status'] } | null>(null);
    const [masDatos, setMasDatos] = useState(false);

    const [slugNuevo, setSlugNuevo] = useState('');
    const [comprobacion, setComprobacion] = useState<Disponibilidad | null>(null);
    const [comprobando, setComprobando] = useState(false);
    const consultaSlug = useRef<AbortController | null>(null);

    const [exportacion, setExportacion] = useState<Exportacion | null>(null);

    const [sectores, setSectores] = useState<Sector[]>([]);
    const [sector, setSector] = useState('');
    /** Color de marca (G803): `#RRGGBB` o vacío (los colores de Reservaria). */
    const [color, setColor] = useState('');
    const [privacidad, setPrivacidad] = useState<{ datos: PaginaPrivacidad; version: string | null } | null>(null);
    const [fichaPriv, setFichaPriv] = useState<FichaPrivacidad>(PRIVACIDAD_VACIA);
    const [encargo, setEncargo] = useState<ContratoEncargo | null>(null);
    const abiertaDesdeRuta = useRef(false);

    // 3.3 «Cuándo se puede reservar»: la tarjeta y el horario solo si el servidor manda sus entradas (G130, G244).
    const entradaRegla = anidadas?.find((e) => e.clave === 'reglas_reserva' && e.anidadaComo === 'card' && !e.noContratada);
    const entradaHorario = anidadas?.find((e) => e.clave === 'horario' && e.anidadaComo === 'section' && !e.noContratada);
    const conRegla = entradaRegla !== undefined;
    // «Políticas de pago» (Pagos, G130): solo si el servidor manda su tarjeta; lleva a su ruta (Pagos › Políticas).
    const entradaPoliticas = anidadas?.find((e) => e.clave === 'politicas_pago' && e.anidadaComo === 'card' && !e.noContratada);
    // «Para desarrolladores» (Web pública, G130, G635): solo si el servidor manda su tarjeta; lleva a su ruta (las claves de API, 13.6).
    const entradaDesarrolladores = anidadas?.find((e) => e.clave === 'desarrolladores' && e.anidadaComo === 'card' && !e.noContratada);
    // G947: las fichas del email y del bot de Telegram para sus filas del panel de accesos (si los hay y se pueden ver) y
    // las cifras de estado de cada fila (`cifrasDeAccesos`), leídas con el permiso de su pantalla de destino.
    const [canalesDev, setCanalesDev] = useState<{ email: string | null; telegram: string | null }>({ email: null, telegram: null });
    const [cifrasDev, setCifrasDev] = useState<Record<string, CifraAcceso>>({});
    const verCanales = permisos.puede('canales.salud.ver');
    const conPanelDev = lado === 'dev';
    const puedeDev = permisos.puede;
    useEffect(() => {
        if (!conPanelDev) return;
        let vivo = true;
        const si = <T,>(cond: boolean, leer: () => Promise<T>): Promise<T | null> => (cond ? leer().catch(() => null) : Promise.resolve(null));
        // Todas las páginas (como mucho 10 de 100), para que la cifra no se quede en la primera.
        const todas = async <T,>(pagina: (cursor: string | undefined) => Promise<{ data: T[]; next_cursor?: string | null }>): Promise<T[]> => {
            const filas: T[] = [];
            let cursor: string | undefined;
            for (let vuelta = 0; vuelta < 10; vuelta++) {
                const r = await pagina(cursor);
                filas.push(...r.data);
                if (r.next_cursor == null) break;
                cursor = r.next_cursor;
            }
            return filas;
        };
        const conCursor = (cursor: string | undefined) => ({ limit: 100, ...(cursor === undefined ? {} : { cursor }) });
        void Promise.all([
            si(puedeDev('api.claves.gestionar'), () => todas((c) => ops.listApiKeys(negocio, conCursor(c)))),
            si(puedeDev('web.widgets.gestionar'), () => todas((c) => ops.listWidgets(negocio, conCursor(c)))),
            si(verCanales, () => ops.listChannels(negocio).then((l) => l.data)),
            si(puedeDev('canales.plantillas.gestionar'), () => todas((c) => ops.listMetaTemplates(negocio, conCursor(c)))),
            puedeDev('pagos.cuenta.ver')
                ? ops.getPaymentAccount(negocio).then((r) => r.datos.status, (e: unknown) => (e instanceof ErrorApi && e.estado === 404 ? ('sin' as const) : null))
                : Promise.resolve(null),
            si(puedeDev('equipo.tablets.gestionar'), () => todas((c) => ops.listDevices(negocio, conCursor(c)))),
        ]).then(([claves, widgets, canales, plantillas, cuenta, tablets]) => {
            if (!vivo) return;
            const de = (tipo: string) => canales?.find((c) => c.channel_type === tipo && c.status !== 'disconnected')?.id ?? null;
            setCanalesDev({ email: de('email'), telegram: de('telegram') });
            setCifrasDev(cifrasDeAccesos({ claves, widgets, canales, plantillas, cuenta, tablets }, Date.now()));
        });
        return () => { vivo = false; };
    }, [ops, negocio, conPanelDev, verCanales, puedeDev]);
    const [regla, setRegla] = useState<ReglaConVersion | null>(null);
    const [reglaAbierta, setReglaAbierta] = useState(false);
    const [intentoRegla, setIntentoRegla] = useState(0);
    const reglaDesdeRuta = useRef(false);

    const cargarNegocio = useCallback(() => ops.getBusinessConVersion(negocio).then(setBiz), [ops, negocio]);
    const cargarSedes = useCallback(
        () =>
            ops.listLocations(negocio, { limit: 100 }).then((l) => {
                setSedes(l.data);
            }),
        [ops, negocio],
    );
    const cargarHistorial = useCallback(
        () =>
            ops.listSlugHistory(negocio).then((h) => {
                setHistorial(h.data);
            }),
        [ops, negocio],
    );

    useEffect(() => {
        let vivo = true;
        Promise.all([
            ops.getBusinessConVersion(negocio),
            ops.listLocations(negocio, { limit: 100 }),
            ops.listSlugHistory(negocio),
            ops.listEntitlements(negocio).catch(() => null),
            ops.getBusinessPrivacyPage(negocio).catch(() => null),
        ])
            .then(([b, s, h, e, p]) => {
                if (!vivo) {
                    return;
                }
                setBiz(b);
                setSedes(s.data);
                setHistorial(h.data);
                setBloques((e?.entitlements ?? []).map((x) => x.product_code));
                setPrivacidad(p);
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setErrorCarga(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento]);

    // La lista de sectores (G270), en el idioma del negocio; si no llega, el sector se ve con su código.
    const idiomaNegocio = biz?.datos.locale;
    useEffect(() => {
        if (idiomaNegocio === undefined) {
            return;
        }
        let vivo = true;
        ops.listBusinessSectors(idiomaNegocio)
            .then((l) => {
                if (vivo) {
                    setSectores(l.data);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, idiomaNegocio]);

    useEffect(() => {
        if (!conRegla) {
            return;
        }
        let vivo = true;
        ops.listBookingRules(negocio, { target_kind: 'business', limit: 1 })
            .then((l) => {
                const r = l.data[0];
                if (vivo) {
                    setRegla(r === undefined ? null : { datos: r, version: versionDe(r) });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, conRegla, intentoRegla]);

    // `?pantalla=antelacion` abre «Cuándo se puede reservar» una vez (la regla propia de Recursos y Servicios lleva aquí).
    useEffect(() => {
        if (busqueda.get('pantalla') !== 'antelacion' || !conRegla || reglaDesdeRuta.current) {
            return;
        }
        reglaDesdeRuta.current = true;
        setReglaAbierta(true);
    }, [busqueda, conRegla]);

    // El contrato de encargo solo lo ven quienes cambian los ajustes; sin versión cargada (404), no se pinta.
    const cargarEncargo = useCallback(() => {
        ops.getDataProcessingAgreement(negocio)
            .then(setEncargo)
            .catch(() => {
                setEncargo(null);
            });
    }, [ops, negocio]);

    // El paso «Tu página de privacidad» de la puesta en marcha lleva aquí con `?pantalla=privacidad` (G267): se abre su
    // panel una vez, en cuanto está leída la página.
    const pantallaPedida = busqueda.get('pantalla');
    useEffect(() => {
        if (pantallaPedida !== 'privacidad' || privacidad === null || abiertaDesdeRuta.current) {
            return;
        }
        abiertaDesdeRuta.current = true;
        setFichaPriv(privacidadDeLaPagina(privacidad.datos));
        setIntentado(false);
        setErrApi({});
        setLado('priv');
        setAbierto(true);
        if (puedeEditar) {
            cargarEncargo();
        }
    }, [pantallaPedida, privacidad, puedeEditar, cargarEncargo]);

    // Al volver a la pantalla, la exportación en curso o lista sale sola (G338): la más reciente, si no ha caducado ni fallado.
    useEffect(() => {
        if (!puedeExportar) {
            return;
        }
        let vivo = true;
        ops.listBusinessExports(negocio, { limit: 1 })
            .then((l) => {
                const ultima = l.data[0];
                if (vivo && ultima !== undefined && (ultima.status === 'collecting' || ultima.status === 'ready')) {
                    setExportacion((actual) => actual ?? ultima);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puedeExportar]);

    // Exportación en marcha: se pregunta cada 3 s hasta que esté (G220).
    useEffect(() => {
        if (exportacion?.status !== 'collecting') {
            return;
        }
        const t = setInterval(() => {
            ops.getBusinessExport(negocio, exportacion.id).then(setExportacion).catch(() => undefined);
        }, 3000);
        return () => {
            clearInterval(t);
        };
    }, [ops, negocio, exportacion]);

    useEffect(
        () => () => {
            consultaSlug.current?.abort();
        },
        [],
    );

    const citas = bloques.includes('citas') && !bloques.includes('reservas');
    const ayudaRegla: TarjetaAyuda = citas
        ? { icono: 'hourglass', titulo: 'Cuándo se puede pedir cita', texto: 'La regla general: con cuánta antelación y hasta cuántos días vista. Un servicio puede tener la suya en su ficha.' }
        : { icono: 'hourglass', titulo: 'Cuándo se puede reservar', texto: 'La regla general: con cuánta antelación y hasta cuántos días vista. Una zona, un recurso o un servicio puede tener la suya en su ficha.' };
    useCabecera({ ayuda: [AYUDA_DATOS, AYUDA_SEDES, AYUDA_DIRECCION, AYUDA_PRIVACIDAD, ...(conRegla ? [ayudaRegla] : []), ...(puedeExportar ? [AYUDA_EXPORTAR] : [])], acciones: [] });

    const falla = (e: unknown) => {
        if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
            avisar({ tipo: 'error', texto: TEXTO_CONFLICTO });
            void cargarNegocio().catch(() => undefined);
            void cargarSedes().catch(() => undefined);
            return;
        }
        const campos = erroresDeCampos(e);
        setErrApi(campos);
        if (Object.keys(campos).length > 0) {
            return;
        }
        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido guardar. Prueba de nuevo.') });
    };

    /** Hace la acción con el botón ocupado; devuelve si salió bien. */
    const trabaja = async (accion: () => Promise<void>): Promise<boolean> => {
        setOcupado(true);
        setErrApi({});
        try {
            await accion();
            return true;
        } catch (e) {
            falla(e);
            return false;
        } finally {
            setOcupado(false);
        }
    };

    if (errorCarga !== null) {
        return (
            <EstadoError
                seccion="Negocio"
                codigoSoporte={estadoDeError(errorCarga).codigoSoporte}
                movil={movil}
                onReintentar={() => {
                    setErrorCarga(null);
                    setIntento((i) => i + 1);
                }}
            />
        );
    }
    if (biz === null) {
        return <EstadoCargando seccion="Negocio" />;
    }

    const b = biz.datos;
    const direccion = sinEsquema(membresia?.public_url ?? b.slug);
    const base = baseDeDireccion(membresia?.public_url ?? '', b.slug);
    const gestiona = queGestiona(bloques);
    const ex = textosExportacion(exportacion);
    const rol = membresia?.roles[0]?.name.toLocaleLowerCase('es') ?? null;

    const abrir = (l: Lado) => {
        if (l === 'pagos') {
            if (entradaPoliticas !== undefined) {
                navegar(entradaPoliticas.ruta);
            }
            return;
        }
        if (l === 'dev') {
            // G947: el panel de accesos (antes, G635, iba directo a las claves de API).
            setLado('dev');
            setAbierto(true);
            return;
        }
        if (l === 'regla') {
            setAbierto(false);
            setReglaAbierta(true);
            return;
        }
        setIntentado(false);
        setErrApi({});
        setNombre(b.name);
        setEmail(b.public_email ?? '');
        setTelefono(b.public_phone ?? '');
        setIdioma(b.locale);
        setSector(b.sector);
        setColor(b.brand_color ?? '');
        if (l === 'priv' && privacidad !== null) {
            setFichaPriv(privacidadDeLaPagina(privacidad.datos));
            if (puedeEditar) {
                cargarEncargo();
            }
        }
        if (l === 'slug') {
            setSlugNuevo('');
            setComprobacion(null);
        }
        setLado(l);
        setAbierto(true);
    };

    // Cerrar, el fondo y Esc: en la ficha de una sede vuelven a la lista de Sedes; en lo demás, cierran.
    const cerrar = () => {
        if (lado === 'sede') {
            setLado('se');
            return;
        }
        setAbierto(false);
    };

    const nombreSector = sectores.find((x) => x.code === b.sector)?.name ?? b.sector;
    const tarjetas: { k: Lado; icono: NombreIcono; titulo: string; resumen: string }[] = [
        { k: 'id', icono: 'store', titulo: 'Nombre y sector', resumen: `${b.name} · ${nombreSector}` },
        ...(gestiona === '' ? [] : [{ k: 'ge' as const, icono: 'layers' as const, titulo: 'Qué gestiona', resumen: gestiona }]),
        { k: 'se', icono: 'map-pin', titulo: 'Sedes', resumen: resumenSedes(sedes) },
        { k: 'co', icono: 'phone', titulo: 'Contacto', resumen: [b.public_email, b.public_phone].filter((x) => x != null && x !== '').join(' · ') || 'Sin email ni teléfono' },
        { k: 'slug', icono: 'link', titulo: 'Dirección web', resumen: direccion },
        ...(privacidad === null ? [] : [{ k: 'priv' as const, icono: 'file-text' as const, titulo: 'Privacidad', resumen: resumenPrivacidad(privacidad.datos) }]),
        ...(conRegla ? [{ k: 'regla' as const, icono: 'hourglass' as const, titulo: citas ? 'Cuándo se puede pedir cita' : 'Cuándo se puede reservar', resumen: resumenRegla(regla?.datos) }] : []),
        ...(entradaPoliticas === undefined ? [] : [{ k: 'pagos' as const, icono: 'shield-check' as const, titulo: entradaPoliticas.etiqueta, resumen: 'Señal, garantía y cargos por no venir' }]),
        ...(entradaDesarrolladores === undefined ? [] : [{ k: 'dev' as const, icono: 'code' as const, titulo: entradaDesarrolladores.etiqueta, resumen: 'Claves de API y la API v1' }]),
        { k: 're', icono: 'globe', titulo: 'Zona horaria e idioma', resumen: `${zonaDe(b.timezone)} · ${etiquetaDe(IDIOMAS, b.locale)}` },
        ...(puedeExportar ? [{ k: 'ex' as const, icono: 'download' as const, titulo: 'Exportar los datos', resumen: ex.resumen }] : []),
    ];

    // ---- Acciones ----
    const guardarNegocio = (cambios: CambiosNegocio) => {
        void trabaja(async () => {
            await ops.updateBusiness(negocio, cambios, biz.version ?? '');
            await cargarNegocio();
            avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
            setAbierto(false);
            // El nombre y la dirección se ven en el marco (selector de negocio).
            recargar();
        });
    };
    const colorMal = color.trim() !== '' && !COLOR_MARCA.test(color.trim());
    const guardarNombre = () => {
        setIntentado(true);
        if (nombre.trim() !== '' && !colorMal) {
            const nuevoColor = color.trim() === '' ? null : color.trim();
            guardarNegocio({
                name: nombre.trim(),
                ...(sector !== '' && sector !== b.sector ? { sector } : {}),
                ...(nuevoColor !== (b.brand_color ?? null) ? { brand_color: nuevoColor } : {}),
            });
        }
    };
    const fallosContacto = erroresContacto(email, telefono);
    const guardarContacto = () => {
        setIntentado(true);
        if (fallosContacto.email === undefined && fallosContacto.phone === undefined) {
            guardarNegocio({
                ...(email.trim() === '' ? {} : { public_email: email.trim() }),
                ...(telefono.trim() === '' ? {} : { public_phone: telefonoE164(telefono) }),
            });
        }
    };

    const abrirSede = (s: Sede | null) => {
        setIntentado(false);
        setErrApi({});
        if (s === null) {
            setFicha(FICHA_VACIA);
            setSedeAbierta(null);
            setMasDatos(false);
            setLado('sede');
            return;
        }
        // G228: la sede se lee antes de editarla, para tener su versión.
        void trabaja(async () => {
            const leida = await ops.getLocation(negocio, s.id);
            const f = fichaDe(leida.datos);
            setFicha(f);
            setSedeAbierta({ id: leida.datos.id, version: leida.version, status: leida.datos.status });
            setMasDatos(f.address_line2 !== '' || f.region !== '' || f.region_code !== '' || f.phone !== '');
            setLado('sede');
        });
    };
    const fallosSede = erroresSede(ficha);
    const guardarSede = () => {
        setIntentado(true);
        if (Object.keys(fallosSede).length > 0) {
            return;
        }
        void trabaja(async () => {
            if (sedeAbierta === null) {
                await ops.createLocation(negocio, datosDeFicha(ficha));
            } else {
                await ops.updateLocation(negocio, sedeAbierta.id, datosDeFicha(ficha), sedeAbierta.version ?? '');
            }
            await cargarSedes();
            avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
            setLado('se');
        });
    };
    const cambiarEstadoSede = (status: Sede['status']) => {
        if (sedeAbierta === null) {
            return;
        }
        void trabaja(async () => {
            await ops.updateLocation(negocio, sedeAbierta.id, { ...datosDeFicha(ficha), status }, sedeAbierta.version ?? '');
            await cargarSedes();
            setLado('se');
        }).then(() => {
            setVentana(null);
        });
    };

    const escribirSlug = (v: string) => {
        const nuevo = normalizarSlug(v);
        setSlugNuevo(nuevo);
        setErrApi({});
        consultaSlug.current?.abort();
        if (nuevo === '' || nuevo === b.slug || !FORMATO_SLUG.test(nuevo)) {
            setComprobando(false);
            return;
        }
        const control = new AbortController();
        consultaSlug.current = control;
        setComprobando(true);
        // Se comprueba al dejar de escribir (G180); no reserva nada: changeSlug lo vuelve a comprobar.
        setTimeout(() => {
            if (control.signal.aborted) {
                return;
            }
            ops.checkSlugAvailability(nuevo, control.signal)
                .then((r) => {
                    if (!control.signal.aborted) {
                        setComprobacion(r);
                        setComprobando(false);
                    }
                })
                .catch(() => {
                    if (!control.signal.aborted) {
                        setComprobando(false);
                    }
                });
        }, 450);
    };
    const slug = estadoSlug(slugNuevo, b.slug, base, comprobando, comprobacion);
    // Los cambios que quedan este año los cuenta el servidor (G1007, G1035).
    const quedanSlug = b.slug_changes_remaining;
    const cambiosSlug = textosCambiosSlug(quedanSlug);
    const revisarSlug = () => {
        if (slug.ok) {
            setVentana('impacto');
        }
    };
    const confirmarSlug = () => {
        void trabaja(async () => {
            await ops.changeSlug(negocio, slugNuevo);
            await Promise.all([cargarNegocio(), cargarHistorial()]);
            avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
            setAbierto(false);
            recargar();
        }).then(() => {
            setVentana(null);
        });
    };

    // ---- Privacidad (G268): con la plantilla, los cuatro datos del responsable; con la suya, una dirección https ----
    const fallosPriv = erroresPrivacidad(fichaPriv);
    const guardarPrivacidad = () => {
        setIntentado(true);
        if (privacidad === null || Object.keys(fallosPriv).length > 0) {
            return;
        }
        const f = fichaPriv;
        const opcionales = Object.fromEntries(Object.entries({ dpo_contact: f.dpo_contact.trim(), extra_text: f.extra_text.trim() }).filter(([, v]) => v !== ''));
        const cambio =
            f.modo === 'external'
                ? { mode: 'external' as const, external_url: urlDePolitica(f.url) }
                : { mode: 'hosted' as const, values: { legal_name: f.legal_name.trim(), tax_id: f.tax_id.trim(), address: f.address.trim(), contact_email: f.contact_email.trim(), ...opcionales } };
        void trabaja(async () => {
            setPrivacidad(await ops.updateBusinessPrivacyPage(negocio, cambio, privacidad.version ?? ''));
            avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
            setAbierto(false);
        });
    };

    const exportar = () => {
        if (exportacion?.status === 'ready' && exportacion.download_url != null) {
            window.open(exportacion.download_url, '_blank', 'noopener');
            return;
        }
        void trabaja(async () => {
            setExportacion(await ops.createBusinessExport(negocio));
        });
    };

    // ---- Panel abierto ----
    const errorDe = (campo: string, local?: string) => errApi[campo] ?? (intentado ? local : undefined);
    const pagina = privacidad?.datos ?? null;
    const sinCompletar = pagina?.mode === 'auto';
    const alojada = `${base}${b.slug}/privacidad`;
    const ponPriv = (k: keyof FichaPrivacidad) => (v: string) => {
        setFichaPriv((f) => ({ ...f, [k]: v }));
    };
    // Quien solo consulta no ve los campos de una página que nadie ha completado (como en el diseño).
    const verCamposPriv = puedeEditar || !sinCompletar;
    const sinCambio = () => undefined;
    const archivada = sedeAbierta?.status === 'archived';

    const paneles: Record<Exclude<Lado, 'regla' | 'pagos'>, () => Panel> = {
        dev: () => ({
            titulo: entradaDesarrolladores?.etiqueta ?? 'Para desarrolladores',
            eyebrow: 'Accesos directos',
            sub: 'Cada fila te lleva a la pantalla donde se cambia.',
            icono: 'code',
            cuerpo: (
                <div className="rv-neg-campos">
                    {accesosDesarrolladores(permisos.puede, puedeExportar, canalesDev).map((g) => (
                        <section key={g.grupo} aria-label={g.grupo}>
                            <h3 className="rv-neg-linea__k">{g.grupo}</h3>
                            <div className="rv-neg-lineas">
                                {g.filas.map((f) => (
                                    <button
                                        key={f.k}
                                        type="button"
                                        className="rv-neg-linea rv-neg-linea--boton"
                                        onClick={() => {
                                            if (f.ruta === null) {
                                                setLado('ex');
                                            } else {
                                                navegar(f.ruta);
                                            }
                                        }}
                                    >
                                        <span className="rv-neg-linea__icono"><Icono nombre={f.icono} /></span>
                                        <span className="rv-neg-linea__texto">
                                            <strong>{f.nombre}</strong>
                                            <span>{cifrasDev[f.k]?.linea ?? f.linea}</span>
                                        </span>
                                        {(cifrasDev[f.k]?.estado !== undefined || cifrasDev[f.k]?.chip !== undefined) && (
                                            <span className="rv-neg-linea__cifras">
                                                {cifrasDev[f.k]?.estado !== undefined && <span className="rv-neg-linea__cifra">{cifrasDev[f.k]?.estado}</span>}
                                                {cifrasDev[f.k]?.chip !== undefined && <Chip estado={cifrasDev[f.k]?.chip?.[1] ?? 'neutral'}>{cifrasDev[f.k]?.chip?.[0]}</Chip>}
                                            </span>
                                        )}
                                        <Icono nombre="chevron-right" />
                                    </button>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            ),
        }),
        id: () => ({
            titulo: 'Nombre y sector',
            eyebrow: 'Datos generales',
            sub: puedeEditar ? 'Sale en tu web, tus mensajes y tus pases.' : 'Solo lectura.',
            icono: 'store',
            ...(puedeEditar ? { accion: 'Guardar', onAccion: guardarNombre } : {}),
            cuerpo: (
                <div className="rv-neg-campos">
                    <Campo etiqueta="Nombre del negocio" valor={nombre} onChange={setNombre} bloqueado={!puedeEditar} max={120} autocompletar="organization" error={errorDe('name', nombre.trim() === '' ? 'Escribe el nombre del negocio.' : undefined)} ayuda="Sale en tu web, tus mensajes y tus pases." onEnter={guardarNombre} />
                    <Seleccion etiqueta="Sector" valor={sector} opciones={opcionesSector(sectores, b.sector)} onChange={setSector} bloqueado={!puedeEditar} ayuda={errApi.sector ?? 'Fija las palabras por defecto del panel.'} />
                    {/* Color de marca (G803): lo usan la página del pase, las carteras y, al crearla, tu web. */}
                    <div className="rv-neg-color">
                        <i style={{ background: COLOR_MARCA.test(color.trim()) ? color.trim() : 'transparent' }} aria-hidden="true" />
                        <Campo etiqueta="Color de marca" valor={color} onChange={setColor} bloqueado={!puedeEditar} max={7} error={errorDe('brand_color', colorMal ? 'Escríbelo así: #DD1420' : undefined)} ayuda="Opcional. Sale en tus pases y en las carteras del móvil." />
                    </div>
                </div>
            ),
        }),
        ge: () => ({
            titulo: 'Qué gestiona',
            eyebrow: 'Datos generales',
            sub: 'Lo que ofreces a tus clientes.',
            icono: 'layers',
            cuerpo: (
                <div className="rv-neg-campos">
                    {/* Añadir o quitar Reservas y Citas (addSubscriptionItem, G170) llega con la entrega de Planes. */}
                    <Campo etiqueta="Qué gestiona el negocio" valor={gestiona} onChange={sinCambio} bloqueado />
                </div>
            ),
        }),
        co: () => ({
            titulo: 'Contacto',
            eyebrow: 'Datos generales',
            sub: puedeEditar ? 'Para que tus clientes te escriban o te llamen.' : 'Solo lectura.',
            icono: 'phone',
            ...(puedeEditar ? { accion: 'Guardar', onAccion: guardarContacto } : {}),
            cuerpo: (
                <div className="rv-neg-campos">
                    <Campo etiqueta="Email para tus clientes" tipo="email" valor={email} onChange={setEmail} bloqueado={!puedeEditar} error={errorDe('public_email', fallosContacto.email)} ayuda="Opcional. Sale en tu web y en los mensajes." />
                    <Campo etiqueta="Teléfono para tus clientes" tipo="tel" valor={telefono} onChange={setTelefono} bloqueado={!puedeEditar} error={errorDe('public_phone', fallosContacto.phone)} ayuda="Opcional. Con prefijo, por ejemplo +34." />
                </div>
            ),
        }),
        re: () => ({
            titulo: 'Zona horaria e idioma',
            eyebrow: 'Datos generales',
            ...(puedeEditar
                ? {
                      accion: 'Guardar',
                      onAccion: () => {
                          guardarNegocio({ locale: idioma });
                      },
                  }
                : { sub: 'Solo lectura.' }),
            icono: 'globe',
            cuerpo: (
                <div className="rv-neg-campos">
                    {/* G255: la zona horaria se cambia en Horario (3.1), con vista previa. */}
                    <Campo etiqueta="Zona horaria" valor={zonaDe(b.timezone)} onChange={sinCambio} bloqueado ayuda="Se cambia en Horario." />
                    {entradaHorario !== undefined && (
                        <Boton
                            tipo="secundario"
                            icono="clock"
                            onClick={() => {
                                navegar(entradaHorario.ruta);
                            }}
                        >
                            Ir a Horario
                        </Boton>
                    )}
                    <Seleccion etiqueta="Idioma" valor={idioma} opciones={IDIOMAS} onChange={setIdioma} bloqueado={!puedeEditar} ayuda="De tu web y de los mensajes a tus clientes." />
                    <Campo etiqueta="País y moneda" valor={`${etiquetaDe(PAISES, b.country)} · ${b.currency}`} onChange={sinCambio} bloqueado ayuda="Se eligen al crear el negocio y no se cambian." />
                </div>
            ),
        }),
        se: () => ({
            titulo: 'Sedes',
            eyebrow: 'Sedes',
            sub: resumenSedes(sedes),
            icono: 'map-pin',
            ...(puedeEditar
                ? {
                      accion: 'Añadir sede',
                      accionIcono: 'plus',
                      onAccion: () => {
                          abrirSede(null);
                      },
                  }
                : {}),
            cuerpo: (
                <div className="rv-neg-lineas">
                    {sedes.map((s) => (
                        <div key={s.id} className="rv-neg-linea">
                            <span className="rv-neg-linea__icono">
                                <Icono nombre={s.status === 'archived' ? 'archive' : 'map-pin'} />
                            </span>
                            <span className="rv-neg-linea__texto">
                                <span className="rv-neg-linea__nombre">{s.name}</span>
                                <strong style={{ color: s.status === 'archived' ? 'var(--rv-fg-3)' : undefined }}>{direccionDeSede(s)}</strong>
                            </span>
                            {s.status === 'archived' && <Chip estado="neutral">Archivada</Chip>}
                            <Boton
                                tipo="secundario"
                                icono={puedeEditar ? 'pencil' : 'eye'}
                                aria-label={`${puedeEditar ? 'Editar' : 'Ver'} ${s.name}`}
                                onClick={() => {
                                    abrirSede(s);
                                }}
                            >
                                {puedeEditar ? 'Editar' : 'Ver'}
                            </Boton>
                        </div>
                    ))}
                </div>
            ),
        }),
        sede: () => ({
            titulo: sedeAbierta === null ? 'Nueva sede' : ficha.name,
            eyebrow: 'Sedes',
            sub: sedeAbierta === null ? 'Dirección que ven tus clientes.' : archivada ? 'Archivada' : [ficha.address_line1, ficha.city].join(', '),
            icono: 'map-pin',
            cerrar: 'Sedes',
            ...(puedeEditar ? { accion: 'Guardar', onAccion: guardarSede } : {}),
            cuerpo: (
                <div className="rv-neg-campos">
                    <nav className="rv-neg-migas" aria-label="Dónde estás">
                        <button
                            type="button"
                            onClick={() => {
                                setLado('se');
                            }}
                        >
                            ‹ Sedes
                        </button>
                        <span aria-hidden="true">›</span>
                        <span>{sedeAbierta === null ? 'Nueva sede' : ficha.name}</span>
                    </nav>
                    <Campo etiqueta="Nombre de la sede" valor={ficha.name} onChange={(v) => { setFicha({ ...ficha, name: v }); }} bloqueado={!puedeEditar} max={120} error={errorDe('name', fallosSede.name)} />
                    <Campo etiqueta="Dirección" valor={ficha.address_line1} onChange={(v) => { setFicha({ ...ficha, address_line1: v }); }} bloqueado={!puedeEditar} autocompletar="address-line1" error={errorDe('address_line1', fallosSede.address_line1)} />
                    <div className="rv-neg-dos">
                        <Campo etiqueta="Código postal" valor={ficha.postal_code} onChange={(v) => { setFicha({ ...ficha, postal_code: v }); }} bloqueado={!puedeEditar} autocompletar="postal-code" modo="numeric" max={10} error={errorDe('postal_code', fallosSede.postal_code)} />
                        <Campo etiqueta="Ciudad" valor={ficha.city} onChange={(v) => { setFicha({ ...ficha, city: v }); }} bloqueado={!puedeEditar} autocompletar="address-level2" error={errorDe('city', fallosSede.city)} />
                    </div>
                    <Seleccion etiqueta="País" valor={ficha.country} opciones={PAISES} onChange={(v) => { setFicha({ ...ficha, country: v, region_code: v === 'ES' ? ficha.region_code : '' }); }} bloqueado={!puedeEditar} />
                    {masDatos || !puedeEditar ? (
                        <>
                            <Campo etiqueta="Piso, puerta… (opcional)" valor={ficha.address_line2} onChange={(v) => { setFicha({ ...ficha, address_line2: v }); }} bloqueado={!puedeEditar} autocompletar="address-line2" />
                            <Campo etiqueta="Provincia (opcional)" valor={ficha.region} onChange={(v) => { setFicha({ ...ficha, region: v }); }} bloqueado={!puedeEditar} autocompletar="address-level1" />
                            {/* G251: la comunidad (ISO 3166-2) para los festivos autonómicos; por ahora, la lista de España. */}
                            {ficha.country === 'ES' && (
                                <Seleccion etiqueta="Comunidad (opcional)" valor={ficha.region_code} opciones={[['', 'Sin elegir'], ...COMUNIDADES_ES]} onChange={(v) => { setFicha({ ...ficha, region_code: v }); }} bloqueado={!puedeEditar} ayuda="Para ofrecerte los festivos de tu comunidad." />
                            )}
                            <Campo etiqueta="Teléfono de la sede (opcional)" tipo="tel" valor={ficha.phone} onChange={(v) => { setFicha({ ...ficha, phone: v }); }} bloqueado={!puedeEditar} error={errApi.phone} />
                        </>
                    ) : (
                        <button
                            type="button"
                            className="rv-neg-mas"
                            onClick={() => {
                                setMasDatos(true);
                            }}
                        >
                            <Icono nombre="plus" />
                            <span>Añadir piso, provincia o teléfono</span>
                        </button>
                    )}
                    {puedeEditar && sedeAbierta !== null && (
                        <div className="rv-neg-archivar">
                            <span className="rv-neg-linea__texto">
                                <strong>{archivada ? 'Reactivar la sede' : 'Archivar la sede'}</strong>
                                <span>{archivada ? 'Vuelve a aparecer al elegir sede.' : 'No se borra: las reservas y el equipo que la usan se mantienen.'}</span>
                            </span>
                            <Boton
                                tipo="secundario"
                                rojo={!archivada}
                                icono={archivada ? 'archive-restore' : 'archive'}
                                onClick={() => {
                                    if (archivada) {
                                        cambiarEstadoSede('active');
                                    } else {
                                        setVentana('archivar');
                                    }
                                }}
                            >
                                {archivada ? 'Reactivar' : 'Archivar'}
                            </Boton>
                        </div>
                    )}
                </div>
            ),
        }),
        slug: () => ({
            titulo: puedeSlug ? 'Cambiar la dirección' : 'Dirección web',
            eyebrow: 'Dirección web',
            icono: 'link',
            // G1007: con cambios, el campo y «Revisar el cambio»; sin ninguno, el aviso en su lugar y sin acción.
            ...(puedeSlug
                ? { sub: cambiosSlug.sub, ...(quedanSlug > 0 ? { accion: 'Revisar el cambio', accionIcono: 'arrow-right' as const, inactiva: !slug.ok, onAccion: revisarSlug } : {}) }
                : { sub: 'Solo lectura.' }),
            cuerpo: (
                <div className="rv-neg-campos">
                    <div className="rv-neg-actual">
                        <span>Dirección actual</span>
                        <strong>{direccion}</strong>
                    </div>
                    {puedeSlug && quedanSlug > 0 && (
                        <Campo etiqueta="Dirección nueva" tipo="url" prefijo={base} valor={slugNuevo} onChange={escribirSlug} onEnter={revisarSlug} error={errApi.slug ?? slug.error} ayuda={slug.ayuda} />
                    )}
                    {puedeSlug && quedanSlug <= 0 && (
                        <div className="rv-neg-sin-cambios" role="status">
                            <Icono nombre="lock" />
                            <span>{TEXTO_SIN_CAMBIOS_SLUG}</span>
                        </div>
                    )}
                    <div className="rv-neg-lineas">
                        <Linea icono="refresh-cw" clave="Cambios este año" valor={cambiosSlug.usados} chip={cambiosSlug.chip} chipEstado={cambiosSlug.chipEstado} />
                        {historial
                            .filter((h) => !h.current)
                            .map((h) => (
                                <div key={h.slug} className="rv-neg-linea-con-pie">
                                    <Linea icono="history" clave={h.valid_to == null ? 'Antes' : `Antes, hasta ${mesYAno(h.valid_to)}`} valor={`${base}${h.slug}`} chip="Redirige" />
                                    {/* G1035: «Recuperar» la escribe en el campo; con cambios este año y quien puede cambiarla. */}
                                    {puedeSlug && quedanSlug > 0 && (
                                        <span className="rv-neg-recuperar">
                                            <button
                                                type="button"
                                                className="rv-neg-recuperar__boton"
                                                aria-label={`Recuperar ${base}${h.slug}`}
                                                onClick={() => {
                                                    escribirSlug(h.slug);
                                                }}
                                            >
                                                <Icono nombre="rotate-ccw" />
                                                Recuperar
                                            </button>
                                            <span>{TEXTOS_SLUG.recuperar}</span>
                                        </span>
                                    )}
                                </div>
                            ))}
                        {puedeSlug && (
                            <>
                                <Linea icono="history" texto={`${direccion} seguirá llevando a la nueva.`} />
                                <Linea icono="qr-code" texto="Los QR y enlaces que ya hayas repartido siguen funcionando." />
                            </>
                        )}
                    </div>
                </div>
            ),
        }),
        priv: () => ({
            titulo: 'Privacidad',
            eyebrow: 'Datos generales',
            sub: 'La que enlazan tu web y tus páginas para clientes.',
            icono: 'file-text',
            ...(puedeEditar ? { accion: sinCompletar && fichaPriv.modo === 'hosted' ? 'Publicar' : 'Guardar', onAccion: guardarPrivacidad } : {}),
            cuerpo: (
                <div className="rv-neg-campos">
                    <div className="rv-neg-priv">
                        <span className="rv-neg-priv__cabeza">
                            <span>{`Lo que enlaza «Privacidad de ${b.name}»`}</span>
                            <Chip estado={sinCompletar ? 'problem' : 'confirmed'}>{sinCompletar ? 'Sin completar' : 'Publicada'}</Chip>
                        </span>
                        <strong>{sinEsquema(pagina?.public_url ?? '')}</strong>
                        {/* G1405: la plantilla mínima de privacidad ya está validada por la asesoría; sin el aviso «Versión provisional» (G342). */}
                        <span>{sinCompletar ? 'Tus clientes tienen que poder leer qué haces con sus datos. Completa la página de Reservaria o pon la tuya.' : 'Sale en tu web y en tus páginas para clientes: al identificarse, junto al permiso para ofertas y en el pie.'}</span>
                    </div>
                    <div className="rv-neg-priv__modo">
                        <strong>Qué página enlazamos</strong>
                        {puedeEditar ? (
                            <Seleccion
                                etiqueta="Página"
                                valor={fichaPriv.modo}
                                opciones={[
                                    ['hosted', 'Usar la de Reservaria'],
                                    ['external', 'Tengo la mía'],
                                ]}
                                onChange={(v) => {
                                    setIntentado(false);
                                    setErrApi({});
                                    setFichaPriv((f) => ({ ...f, modo: v === 'external' ? 'external' : 'hosted' }));
                                }}
                            />
                        ) : (
                            <span className="rv-neg-priv__valor">{pagina?.mode === 'external' ? 'Tu propia política' : 'La página de Reservaria'}</span>
                        )}
                        <span>{!puedeEditar ? 'La cambian el propietario y los administradores.' : fichaPriv.modo === 'external' ? 'Enlazamos la que ya tienes en tu web.' : `Una plantilla revisada por la asesoría de Reservaria. Completas quién eres y la publicamos en ${alojada}.`}</span>
                    </div>
                    {verCamposPriv && fichaPriv.modo === 'external' && (
                        <Campo etiqueta="Dirección de tu política" tipo="url" valor={fichaPriv.url} onChange={ponPriv('url')} onEnter={guardarPrivacidad} bloqueado={!puedeEditar} max={300} error={errorDe('external_url', fallosPriv.url)} ayuda="La de tu web, por ejemplo tunegocio.es/privacidad." />
                    )}
                    {verCamposPriv && fichaPriv.modo === 'hosted' && (
                        <>
                            <div className="rv-neg-priv__titulo">
                                <strong>Quién es el responsable</strong>
                                <span>Tus datos legales. Salen al principio de la página.</span>
                            </div>
                            <Campo etiqueta="Razón social o nombre del titular" valor={fichaPriv.legal_name} onChange={ponPriv('legal_name')} bloqueado={!puedeEditar} autocompletar="organization" max={200} error={errorDe('values.legal_name', fallosPriv.legal_name)} ayuda="La empresa, o tu nombre si eres autónomo." />
                            <Campo etiqueta="NIF" valor={fichaPriv.tax_id} onChange={ponPriv('tax_id')} bloqueado={!puedeEditar} mayusculas="characters" max={32} error={errorDe('values.tax_id', fallosPriv.tax_id)} />
                            <Campo etiqueta="Dirección" valor={fichaPriv.address} onChange={ponPriv('address')} bloqueado={!puedeEditar} autocompletar="street-address" max={300} error={errorDe('values.address', fallosPriv.address)} />
                            <Campo etiqueta="Email para temas de privacidad" tipo="email" valor={fichaPriv.contact_email} onChange={ponPriv('contact_email')} bloqueado={!puedeEditar} error={errorDe('values.contact_email', fallosPriv.contact_email)} ayuda="Donde tus clientes piden una copia de sus datos o que los borres." />
                            <Campo etiqueta="Delegado de protección de datos" valor={fichaPriv.dpo_contact} onChange={ponPriv('dpo_contact')} bloqueado={!puedeEditar} max={200} error={errApi['values.dpo_contact']} ayuda="Opcional. Solo si tienes uno: su nombre y cómo contactar." />
                            <details className="rv-neg-plantilla">
                                <summary>
                                    <Icono nombre="file-text" />
                                    <span>
                                        <strong>Lo que pone la plantilla</strong>
                                        <span>Para qué, cuánto tiempo, quién te ayuda y sus derechos</span>
                                    </span>
                                    <Icono nombre="chevron-down" />
                                </summary>
                                <div className="rv-neg-lineas">
                                    {PLANTILLA_PRIVACIDAD.map(([icono, titulo, texto]) => (
                                        <Linea key={titulo} icono={icono} valor={titulo} texto={texto} />
                                    ))}
                                </div>
                                <p className="rv-neg-texto">La redacta la asesoría de Reservaria. Si cambia, tu página se actualiza sola.</p>
                            </details>
                            <Campo etiqueta="Algo más que quieras contar (opcional)" filas={3} valor={fichaPriv.extra_text} onChange={ponPriv('extra_text')} bloqueado={!puedeEditar} max={4000} error={errApi['values.extra_text']} ayuda="Por ejemplo, tus normas de admisión." />
                            {pagina?.mode === 'hosted' && pagina.public_url != null && (
                                <Boton
                                    tipo="secundario"
                                    icono="external-link"
                                    onClick={() => {
                                        window.open(pagina.public_url ?? '', '_blank', 'noopener');
                                    }}
                                >
                                    Ver la página
                                </Boton>
                            )}
                        </>
                    )}
                    {puedeEditar && encargo !== null && (
                        <div className="rv-neg-encargo">
                            <div className="rv-neg-linea">
                                <span className="rv-neg-linea__icono">
                                    <Icono nombre="file-check" />
                                </span>
                                <span className="rv-neg-linea__texto">
                                    <strong>Contrato de encargo</strong>
                                    <span>{`Versión ${encargo.version}, aceptada con las condiciones del negocio. Con la lista de proveedores que tratan datos.`}</span>
                                </span>
                                <Boton
                                    tipo="secundario"
                                    icono="download"
                                    onClick={() => {
                                        window.open(encargo.download_url, '_blank', 'noopener');
                                    }}
                                >
                                    Descargar
                                </Boton>
                            </div>
                            <p className="rv-neg-texto">Reservaria trata los datos de tus clientes por cuenta tuya. Si cambia el contrato o entra un proveedor nuevo, te avisamos 30 días antes y puedes oponerte.</p>
                            {encargo.upcoming_change != null && (
                                // G931: la oposición es por escrito a soporte, con el asunto ya puesto; no hay operación.
                                <p className="rv-neg-texto">
                                    {`Cambio anunciado: versión ${encargo.upcoming_change.version}, desde el ${new Date(encargo.upcoming_change.effective_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}. ${encargo.upcoming_change.summary} Puedes oponerte antes de esa fecha. `}
                                    {/* Solo el Propietario se opone (dibujo de 1.14, G931). */}
                                    {membresia?.is_owner === true && (
                                        <>
                                            <a href={enlaceOposicion(correoDeSoporte(), encargo.upcoming_change.version, { id: negocio, nombre })}>Oponerme</a>
                                            <span>{` Se abre tu correo para escribir a ${correoDeSoporte()}; te contesta una persona.`}</span>
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            ),
        }),
        ex: () => ({
            titulo: ex.titulo,
            eyebrow: 'Tus datos',
            sub: 'Todos los datos del negocio en un archivo.',
            icono: 'download',
            ...(ex.chip === null ? {} : { chip: ex.chip, chipEstado: ex.chipEstado }),
            accion: exportacion?.status === 'ready' ? 'Descargar' : 'Exportar todo',
            accionIcono: 'download',
            inactiva: exportacion?.status === 'collecting',
            onAccion: exportar,
            cuerpo: <p className="rv-neg-texto">{ex.texto}</p>,
        }),
    };
    const panel = lado === null || lado === 'regla' || lado === 'pagos' ? null : paneles[lado]();

    const notaSoloLectura = soloLectura
        ? 'El negocio está en solo lectura: puedes consultar sus datos, pero no cambiarlos.'
        : !puedeEditar
          ? rol === null
              ? 'Puedes consultar los datos del negocio. Los cambian el propietario y los administradores.'
              : `Como ${rol} puedes consultar los datos del negocio. Los cambian el propietario y los administradores.`
          : null;

    return (
        <div className="rv-neg">
            {notaSoloLectura !== null && (
                <p className="rv-neg-nota">
                    <Icono nombre="eye" />
                    <span>{notaSoloLectura}</span>
                </p>
            )}
            <div className="rv-neg-rejilla" role="list" aria-label="Datos del negocio">
                {tarjetas.map((t, i) => {
                    const activa = t.k === 'regla' ? reglaAbierta : abierto && (lado === t.k || (t.k === 'se' && lado === 'sede'));
                    return (
                        <div key={t.k} role="listitem">
                            <button
                                type="button"
                                className={`rv-neg-tarjeta${activa ? ' rv-neg-tarjeta--activa' : ''}`}
                                aria-label={`${t.titulo}, ${t.resumen}`}
                                style={{ animationDelay: `${String(i * 45)}ms` }}
                                onClick={() => {
                                    abrir(t.k);
                                }}
                            >
                                <Icono nombre={t.icono} />
                                <span className="rv-neg-tarjeta__pie">
                                    <span>
                                        <strong>{t.titulo}</strong>
                                        <span>{t.resumen}</span>
                                    </span>
                                    <Icono nombre="chevron-right" />
                                </span>
                            </button>
                        </div>
                    );
                })}
            </div>

            <PanelLateral
                abierto={abierto && panel !== null}
                onCerrar={cerrar}
                movil={movil}
                titulo={panel?.titulo ?? ''}
                eyebrow={panel?.eyebrow}
                subtitulo={panel?.sub}
                icono={panel?.icono}
                accion={panel?.accion ?? ''}
                accionIcono={panel?.accionIcono ?? 'check'}
                inactiva={panel?.inactiva ?? false}
                ocupado={ocupado}
                ocupadoTexto={lado === 'ex' ? 'Preparando…' : 'Guardando…'}
                onAccion={panel?.onAccion}
                cerrar={panel?.cerrar ?? 'Cerrar'}
                chip={panel?.chip}
                chipEstado={panel?.chipEstado ?? 'neutral'}
            >
                {panel?.cuerpo}
            </PanelLateral>

            {conRegla && (
                <ReglaReserva
                    ops={ops}
                    negocio={negocio}
                    abierto={reglaAbierta}
                    onCerrar={() => {
                        setReglaAbierta(false);
                    }}
                    movil={movil}
                    editable={permisos.puede('disponibilidad.reglas.gestionar') && !soloLectura}
                    citas={citas}
                    zona={b.timezone}
                    regla={regla}
                    onGuardada={setRegla}
                    onRecargar={() => {
                        setIntentoRegla((i) => i + 1);
                    }}
                />
            )}

            <Ventana
                abierta={ventana === 'impacto'}
                titulo="Revisar el impacto y confirmar"
                texto="Antes de cambiar la dirección de tu web, mira a qué afecta."
                accion="Confirmar el cambio"
                accionIcono="check"
                cancelar="Volver"
                cancelarIcono="arrow-left"
                ocupado={ocupado}
                ocupadoTexto="Cambiando…"
                ancho="520px"
                movil={movil}
                onAccion={confirmarSlug}
                onCerrar={() => {
                    setVentana(null);
                }}
            >
                <div className="rv-neg-lineas">
                    <Linea icono="link" clave={direccion} valor={`${base}${slugNuevo}`} />
                    {IMPACTO.map(([icono, titulo, texto]) => (
                        <Linea key={titulo} icono={icono} valor={titulo} texto={texto ?? `Quien entre en ${direccion} llega a la nueva. La antigua queda reservada para tu negocio.`} />
                    ))}
                    <Linea icono="refresh-cw" valor="Cambios que quedan este año" texto={cambiosSlug.impacto} />
                </div>
            </Ventana>

            <Ventana
                abierta={ventana === 'archivar'}
                titulo={`Archivar ${ficha.name}`}
                texto="No se borra: las reservas y el equipo que la usan se mantienen. Deja de aparecer al elegir sede y puedes reactivarla cuando quieras."
                accion="Archivar"
                accionIcono="archive"
                ocupado={ocupado}
                ocupadoTexto="Archivando…"
                movil={movil}
                onAccion={() => {
                    cambiarEstadoSede('archived');
                }}
                onCerrar={() => {
                    setVentana(null);
                }}
            />
        </div>
    );
}

/** El `?apartado=` de la ruta de una entrada anidada (`/negocio?apartado=horario`); sin él, su clave. */
function valorDe(clave: string, ruta: string): string {
    return new URLSearchParams(ruta.split('?')[1] ?? '').get('apartado') ?? clave;
}

/** Pantalla de cada apartado de «Negocio» por la clave de su entrada anidada (G130). Sin pantalla, su hueco. */
const APARTADOS_NEGOCIO: Readonly<Partial<Record<string, (p: PropsPantalla) => ReactNode>>> = {
    horario: (p) => <Horario {...p} />,
};

/**
 * «Negocio» (1.14) con su barra de apartados: «Datos del negocio» y los que manda el servidor con `parent_key: negocio` y
 * `nested_as: section` (hoy «Horario», de Agenda y Disponibilidad, G130, G244), en su orden y solo si la persona tiene su
 * permiso. Sin apartados, solo los datos (sin barra). `?apartado=excepciones` abre Horario y baja a sus días especiales.
 */
export function Negocio(props: PropsPantalla) {
    const { busqueda, ruta, navegar, movil } = props;
    const secciones = (props.anidadas ?? []).filter((e) => e.anidadaComo === 'section' && !e.noContratada);
    if (secciones.length === 0) {
        return <DatosDelNegocio {...props} />;
    }
    const apartados: { clave: string; etiqueta: string; corta: string; icono: NombreIcono; valor: string | null }[] = [
        { clave: 'datos', etiqueta: 'Datos del negocio', corta: 'Datos', icono: 'store', valor: null },
        ...secciones.map((e) => ({ clave: e.clave, etiqueta: e.etiqueta, corta: e.etiqueta, icono: dibujoDe(e.icono), valor: valorDe(e.clave, e.ruta) })),
    ];
    const pedido = busqueda.get('apartado');
    const buscado = pedido === 'excepciones' ? 'horario' : pedido;
    const activo = apartados.find((a) => a.valor !== null && a.valor === buscado) ?? apartados[0];
    const indice = activo === undefined ? 0 : apartados.indexOf(activo);
    const Apartado = activo?.valor == null ? undefined : APARTADOS_NEGOCIO[activo.clave];
    return (
        <div className="rv-neg-pantalla">
            <div className="rv-neg-barra">
                <Segmentos
                    etiqueta="Apartados de negocio"
                    movil={movil}
                    items={apartados.map((a) => ({ etiqueta: a.etiqueta, corta: a.corta, icono: a.icono }))}
                    valor={indice}
                    onCambio={(i) => {
                        const a = apartados[i];
                        if (a !== undefined) {
                            navegar(a.valor === null ? ruta : `${ruta}?apartado=${encodeURIComponent(a.valor)}`);
                        }
                    }}
                />
            </div>
            {activo?.valor == null ? <DatosDelNegocio {...props} /> : Apartado !== undefined ? Apartado(props) : <Hueco seccion={activo.etiqueta} icono={activo.icono} />}
        </div>
    );
}
